import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import debounce from "lodash.debounce";
import imageCompression from 'browser-image-compression';

import {
    Button,
    Box,
    Checkbox,
    Container,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material'
import { CheckCircleOutline, DarkMode, ErrorOutline, DarkModeOutlined, LightMode, LightModeOutlined, PersonOutline } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles'

import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import default_avatar from '../../assets/default_avatar.png';
import FileUpload from '../FileUpload/FileUpload';
import buildCardPng, { buildOutgoingCards, cardDataChunkBytes } from '../../utils/buildCardPng';
import getStoredCardData from '../../utils/getStoredCardData';
import formatBytes from '../../utils/formatBytes';
import normalizeCardData, { normalizeLorebook } from '../../utils/normalizeCardData';
import parsePngChunks from '../../utils/parsePngChunks';
import PortraitCompressionDialog from '../PortraitCompressionDialog/PortraitCompressionDialog';
import selectPreferredCardChunk, { countLorebookEntries } from '../../utils/selectPreferredCardChunk';
import stripPngChunks from '../../utils/stripPngChunks';
import { AltGreetingTabPanel, BasicFieldTabPanel, GroupGreetingPanel, LorebookPanel, MacrosPanel, RawJsonPanel } from '../TabPanels/TabPanels';
import { useCard } from '../../context/CardContext';
import { v3CardPrototype } from '../../utils/v3CardPrototype';
import './TavernCardEditor.css';

// Portraits are stored as data URLs, and a large one can exceed the ~5 MB localStorage quota. Clear
// the stored copy in that case, rather than leave a previous card's portrait to be restored on reload.
function storePreviewImage(dataUrl) {
    try {
        localStorage.setItem("previewImage", dataUrl);
    } catch (error) {
        localStorage.removeItem("previewImage");
        console.warn("Portrait is too large to keep across page reloads:", error);
    }
}

// Returns whether the save succeeded. The card's text matters more than the cached portrait, so when
// storage is full, give up the portrait's space and retry before failing.
function storeCardData(cardData) {
    const serialized = JSON.stringify(cardData);
    try {
        localStorage.setItem("cardData", serialized);
        return true;
    } catch {
        localStorage.removeItem("previewImage");
    }
    try {
        localStorage.setItem("cardData", serialized);
        return true;
    } catch (error) {
        console.warn("Card is too large to save in browser storage:", error);
        return false;
    }
}

const TavernCardEditor = ({toggleTheme}) => {
    const theme = useTheme();

    const { cardData, setCardData } = useCard();

    const previewImageRef = useRef(null);
    const [backfillEntriesConfirmation, setBackfillEntriesConfirmation] = useState(false)
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [deleteEntryConfirmation, setDeleteEntryConfirmation] = useState(false);
    const [deleteGreetingConfirmation, setDeleteGreetingConfirmation] = useState(false);
    const [deleteGroupGreetingConfirmation, setDeleteGroupGreetingConfirmation] = useState(false);
    const [deleteLorebookConfirmation, setDeleteLorebookConfirmation] = useState(false);
    const [displayImage, setDisplayImage] = useState(true);
    const [file, setFile] = useState(() => {
        const storedCardData = getStoredCardData();
        return storedCardData === null ? null : {name: storedCardData.data.name};
    });
    const [findReplaceConfirmation, setFindReplaceConfirmation] = useState(false);
    const [overwriteConfirmation, setOverwriteConfirmation] = useState(false);
    const [pendingEntry, setPendingEntry] = useState(-1);
    const [pendingGreeting, setPendingGreeting] = useState(-1);
    const [pendingGroupGreeting, setPendingGroupGreeting] = useState(-1)
    const [pendingFindReplace, setPendingFindReplace] = useState([]);
    const [pendingJson, setPendingJson] = useState(null);
    const [promoteGreeting, setPromoteGreeting] = useState(false);
    const [purgeAsterisksConfirmation, setPurgeAsterisksConfirmation] = useState(false);
    const [preview, setPreview] = useState(default_avatar);
    // A downscaled copy of `preview` chosen in the compression dialog; `preview` itself stays the
    // original so the user can re-pick a size (or revert) without stacking compression losses. Only
    // the applied result is persisted, so after a reload it becomes the new original.
    const [compressedPreview, setCompressedPreview] = useState(null);
    const [compressDialogOpen, setCompressDialogOpen] = useState(false);
    const [portraitBytes, setPortraitBytes] = useState(null);
    const portrait = compressedPreview ?? preview;
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [saveFailed, setSaveFailed] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    const charMetadataFields = [
        {fieldName: "name"},
        {fieldName: "description", multiline:true, rows:10, showCount:true},
        [
            {fieldName: "personality", multiline:true, rows:4},
            {fieldName: "scenario", multiline:true, rows:4}
        ],
        {fieldName: "first_mes", label: "First Message", multiline:true, rows:10, showCount:true},
        {fieldName: "mes_example", label: "Example Messages", multiline:true, rows:10}
    ];

    const creatorMetadataFields = [
        {fieldName: "creator"},
        {fieldName: "character_version", label: "Character Version"},
        {fieldName: "nickname", label: "Nickname"},
        {fieldName: "creator_notes", label: "Creator Notes", multiline:true, rows:10},
        {fieldName: "tags", label: "Tags (Comma separated, no quotes)", multiline:true},
        {fieldName: "source", label: "Source (read-only, tracked automatically)", multiline:true, readOnly:true}
    ];

    const promptFields = [
        {fieldName: "system_prompt", label: "System Prompt", multiline:true, rows:5},
        {fieldName: "post_history_instructions", label: "Post History Instructions", multiline:true, rows:5}
    ]

    const backfillLorebookNames = () => {
        const lorebookEntries = cardData.data.character_book.entries.map((entry) => {
            const newEntry = {...entry};
            if (newEntry.name === "" || !Object.hasOwn(newEntry, "name") || typeof newEntry.name === "undefined") newEntry.name = newEntry.comment;
            else newEntry.comment = newEntry.name;
            return newEntry
        });
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                character_book: {
                    ...prevState.data.character_book,
                    entries: lorebookEntries
                }
            }
        }))
        setBackfillEntriesConfirmation(false);
    };

    const closeDeleteGreetingConfirmation = () => {
        setDeleteGreetingConfirmation(false);
        setPendingGreeting(-1);
    };

    const closeFindReplaceConfirmation = () => {
        setFindReplaceConfirmation(false);
        setPendingFindReplace([]);
    };

    const closeGroupGreetingConfirmation = () => {
        setDeleteGroupGreetingConfirmation(false);
        setPendingGroupGreeting(-1);
    };

    const closeOverwriteConfirmation = () => {
        setOverwriteConfirmation(false);
        setPendingJson(null);
    };

    const closePromoteGreeting = () => {
        setPromoteGreeting(false);
        setPendingGreeting(-1);
    };

    async function convertBufferToBase64(arrayBuffer) {
        return new Promise((resolve) => {
            const blob = new Blob([arrayBuffer], {type: 'image/png'});
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result;
                resolve(base64String);
            }
            reader.readAsDataURL(blob);
        });
    }

    const handleAltGreetingClick = (index) => {
        setPendingGreeting(index);
        setDeleteGreetingConfirmation(true);
    };

    const handleDeleteAltGreeting = () => {
        const altGreetings = [...cardData.data.alternate_greetings];
        altGreetings.splice(pendingGreeting, 1);
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                alternate_greetings: altGreetings
            }
        }));
        setPendingGreeting(-1);
        setDeleteGreetingConfirmation(false);
    }

    const handleDeleteEntry = () => {
        const lorebookEntries = [...cardData.data.character_book.entries];
        lorebookEntries.splice(pendingEntry, 1);
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                character_book: {
                    ...prevState.data.character_book,
                    entries: lorebookEntries
                }
            }
        }));
        setPendingEntry(-1);
        setDeleteEntryConfirmation(false);
    }

    const handleDeleteEntryClick = (index) => {
        setPendingEntry(index);
        setDeleteEntryConfirmation(true);
    }

    const handleDeleteGroupGreeting = () => {
        const groupGreetings = [...cardData.data.group_only_greetings];
        groupGreetings.splice(pendingGroupGreeting, 1);
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                group_only_greetings: groupGreetings
            }
        }));
        setPendingGroupGreeting(-1);
        setDeleteGroupGreetingConfirmation(false);
    };

    const handleDeleteLorebook = () => {
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                character_book: undefined
            }
        }))
        setDeleteLorebookConfirmation(false);
    };

    const loadCardIntoState = (parsedCardData) => {
        const result = normalizeCardData(parsedCardData);
        if (!result.ok) {
            console.error(result.error);
            return;
        }
        setCardData(result.cardData);
        setSaveFailed(!storeCardData(result.cardData));
        if (typeof result.cardData.data.character_book !== "undefined" && result.cardData.data.character_book.entries.length > 0)
            scanLorebookEntryNames(result.cardData.data.character_book.entries);
    };

    async function handleFileSelect(event, importLorebook=false) {
        const selectedFile = event.target.files[0];
        if(!importLorebook) setFile(selectedFile);
        if (/.+\.png$/.test(selectedFile.name)){
            const readCardData = await parsePngChunks(selectedFile, ["ccv3", "chara"]);
            if (!readCardData) {
                console.error("This PNG doesn't have any Card Data!");
                return;
            }

            // Some export tools embed more than one card copy in a PNG and the copies can go out
            // of sync (e.g. one has the full lorebook, another has none/a stale one), so pick
            // whichever chunk actually has the most complete lorebook rather than guessing by
            // keyword or position.
            if (readCardData.length > 1) {
                const entryCounts = readCardData.map((chunk) => `${chunk.keyword}: ${countLorebookEntries(chunk)} lorebook entries`);
                if (new Set(readCardData.map(countLorebookEntries)).size > 1)
                    console.warn(`This PNG has multiple embedded card copies with differing lorebook sizes (${entryCounts.join(", ")}). Using the most complete one.`);
            }
            const preferredChunk = selectPreferredCardChunk(readCardData);
            const parsedCardData = preferredChunk.data;

            if (importLorebook) {
                const lorebookSource = typeof parsedCardData?.data === "object" && parsedCardData.data !== null ? parsedCardData.data.character_book : undefined;
                handleLorebookImportLogic(normalizeLorebook(lorebookSource));
                return;
            }

            console.log(`${preferredChunk.keyword === "ccv3" ? "V3" : "V2"} Card info found: `, parsedCardData);
            loadCardIntoState(parsedCardData);
        } else if (/.+\.json$/.test(selectedFile.name)){
            let parsedJson;
            try {
                parsedJson = JSON.parse(await selectedFile.text());
            } catch (error) {
                console.error("Please upload a valid .json file: ", error);
                return;
            }

            if (importLorebook) {
                const lorebookSource = typeof parsedJson === "object" && parsedJson !== null && parsedJson.spec === "lorebook_v3"
                    ? parsedJson.data
                    : parsedJson?.data?.character_book;
                handleLorebookImportLogic(normalizeLorebook(lorebookSource));
                return;
            }

            loadCardIntoState(parsedJson);
        } else {
            console.error("Please upload a valid card file type (.png or .json)")
        }
    }

    const handleFindReplace = () => {
        const [toFind, toReplace] = pendingFindReplace;
        const newDescription = cardData.data.description.replaceAll(toFind, toReplace);
        const newPersonality = cardData.data.personality.replaceAll(toFind, toReplace);
        const newScenario = cardData.data.scenario.replaceAll(toFind, toReplace);
        const newGreeting = cardData.data.first_mes.replaceAll(toFind, toReplace);
        const newExample = cardData.data.mes_example.replaceAll(toFind, toReplace);
        const newAlternates = cardData.data.alternate_greetings.length > 0 ? [...cardData.data.alternate_greetings].map((greeting) => greeting.replaceAll(toFind, toReplace)) : []
        const newGropGreetings = cardData.data.group_only_greetings.length > 0 ? [...cardData.data.group_only_greetings].map((greeting) => greeting.replaceAll(toFind, toReplace)) : [];
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                description: newDescription,
                personality: newPersonality,
                scenario: newScenario,
                first_mes: newGreeting,
                mes_example: newExample,
                alternate_greetings: newAlternates,
                group_only_greetings: newGropGreetings
            }
        }));
        setFindReplaceConfirmation(false);
        setPendingFindReplace([]);
    }

    const handleFindReplaceClick = (toFind, toReplace) => {
        setPendingFindReplace([toFind, toReplace]);
        setFindReplaceConfirmation(true);
    };

    const handleGroupGreetingClick = (index) => {
        setPendingGroupGreeting(index);
        setDeleteGroupGreetingConfirmation(true);
    };

    const handleJsonDownload = () => {
        const outgoingJson = buildOutgoingCards(cardData).v3;
        const blob = new Blob([JSON.stringify(outgoingJson, null, 4)], { type: 'application/json' });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${outgoingJson.data.name}.json`
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
    };

    const handleLorebookDownload = () =>{
        const outgoingBook = cardData.data.character_book;
        const outgoingJson = {spec: "lorebook_v3", data: outgoingBook};
        const blob = new Blob([JSON.stringify(outgoingJson, null, 4)], { type: 'application/json' });

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${outgoingJson.data.name}.json`
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
    };

    const handleLorebookImportLogic = (newLorebook) => {
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                character_book: newLorebook
            }
        }))
        if (typeof newLorebook !== "undefined" && newLorebook.entries.length > 0)
            scanLorebookEntryNames(newLorebook.entries);
    };

    async function handleOverwriteClick(event) {
        const file = event.target.files[0];
        if (!file) return;
        let parsedJson;
        try {
            parsedJson = JSON.parse(await file.text());
        } catch (error) {
            console.error("Please upload a valid .json file: ", error);
            return;
        }
        const result = normalizeCardData(parsedJson);
        if (!result.ok) {
            console.error(result.error);
            return;
        }
        setPendingJson(result.cardData);
        setOverwriteConfirmation(true);
    }

    const handleOverwriteFile = () => {
        setCardData(pendingJson);
        if (typeof pendingJson.data.character_book !== "undefined" && pendingJson.data.character_book.entries.length > 0)
            scanLorebookEntryNames(pendingJson.data.character_book.entries);
        setOverwriteConfirmation(false);
        setPendingJson(null)
    };

    async function handlePngDownload() {
        try {
            const response = await fetch(portrait);
            if (!response.ok){
                throw new Error("Network response was not OK");
            }
            const respBlob = await response.blob();
            const assembledPng = await buildCardPng(await respBlob.arrayBuffer(), cardData);
            const blob = new Blob([assembledPng], { type: 'image/png' });

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${cardData.data.name}.png`
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("There was an error retrieving the card's PNG: ", error)
        }
    }

    async function handlePreviewUpload (event) {
        const file = event.target.files[0];
        if (!file) return;
        if (file && file.type.startsWith("image/") && file.type !== "image/png") {
            const img = new Image();
            img.src = URL.createObjectURL(file);
            img.onload = async () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");

                ctx.drawImage(img, 0, 0);

                const base64String = canvas.toDataURL("image/png");
                const pngBlob = await (await fetch(base64String)).blob();
                // Compressing converted PNGs
                const compressedPngBlob = await imageCompression(pngBlob, {useWebWorker: true});

                const comrpessedBase64Png = await imageCompression.getDataUrlFromFile(compressedPngBlob);

                setPreview(comrpessedBase64Png);
                storePreviewImage(comrpessedBase64Png);
            }
        }
        else if (file && file.type === "image/png") {
            try {
                const inputBuffer = await readToBuffer(file);
                const arrayBuffer = await stripPngChunks(inputBuffer);
                const pngBlob = new Blob([arrayBuffer], {type: "image/png"});
                const compressedPngBlob = await imageCompression(pngBlob, {useWebWorker: true});

                const comrpessedBase64Png = await imageCompression.getDataUrlFromFile(compressedPngBlob);

                setPreview(comrpessedBase64Png);
                storePreviewImage(comrpessedBase64Png);
            } catch (error) {
                console.error("Error stripping PNG chunks and converting to base64: ", error);
            }
        } else {
            console.error("Invalid file type upload");
            return;
        }
    }

    const handlePromoteClick = (index) => {
        setPendingGreeting(index);
        setPromoteGreeting(true);
    };

    const handlePromoteGreeting = () => {
        const firstMes = cardData.data.first_mes;
        const altGreetings = [...cardData.data.alternate_greetings];
        const [toPromote] = altGreetings.splice(pendingGreeting, 1)
        altGreetings.unshift(firstMes);
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                first_mes: toPromote,
                alternate_greetings: altGreetings
            }
        }))
        setPromoteGreeting(false);
        setPendingGreeting(-1);
    };

    const handlePurgeAsterisks =() => {
        const asteriskRegex = /\*(.+?)\*/g;
        const newGreeting = cardData.data.first_mes.replace(asteriskRegex, '$1');
        const newExample = cardData.data.mes_example.replace(asteriskRegex, '$1');
        const newAlternates = cardData.data.alternate_greetings.length > 0 ? [...cardData.data.alternate_greetings].map((greeting) => greeting.replace(asteriskRegex, '$1')) : []
        const newGropGreetings = cardData.data.group_only_greetings.length > 0 ? [...cardData.data.group_only_greetings].map((greeting) => greeting.replace(asteriskRegex, '$1')) : [];
        setCardData((prevState) => ({
            ...prevState,
            data: {
                ...prevState.data,
                first_mes: newGreeting,
                mes_example: newExample,
                alternate_greetings: newAlternates,
                group_only_greetings: newGropGreetings
            }
        }));
        setPurgeAsterisksConfirmation(false);
    };

    const handleRemoveFile = () => {
        setFile(null);
        setDeleteConfirmation(false);
        setPreview(default_avatar);
        setCardData(v3CardPrototype());
        setSaveFailed(!storeCardData(v3CardPrototype()));
        storePreviewImage(default_avatar);
    };

    const readToBuffer = (infile) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(infile);
        });
    };

    const scanLorebookEntryNames = (lorebook) => {
        for (let i = 0; i < lorebook.length; i++){
            if (lorebook[i].name !== lorebook[i].comment && (lorebook[i].name === "" || lorebook[i].comment === "" || !Object.hasOwn(lorebook[i], "name") || !Object.hasOwn(lorebook[i], "comment"))){
                setBackfillEntriesConfirmation(true);
                return;
            }
        }
    };

    // eslint-disable-next-line
    const debouncedSave = useCallback(
        debounce((data) => {
            if (storeCardData(data)) {
                setSaveFailed(false);
                setLastSavedAt(new Date());
            } else {
                setSaveFailed(true);
            }
        }, 5000), []
    );

    useEffect(() => {
        if (cardData) {
            debouncedSave(cardData);
        }
    }, [cardData, debouncedSave]);

    useEffect(() => {
        (async () => {
            const pngRegex = /.+\.png$/;
            if (!file) {
                setPreview(default_avatar);
                return;
            }
            if (!pngRegex.test(file.name)){
                setPreview(default_avatar);
                return;
            }
            
            const inputBuffer = await readToBuffer(file);
            const strippedBuffer = await stripPngChunks(inputBuffer);
            const base64String = await convertBufferToBase64(strippedBuffer)
            setPreview(base64String);
            storePreviewImage(base64String);
        })()
    }, [file]);

    useEffect(() => {
        setCompressedPreview(null);
    }, [preview]);

    useEffect(() => {
        let cancelled = false;
        fetch(portrait)
            .then((response) => response.arrayBuffer())
            .then(stripPngChunks)
            .then((stripped) => { if (!cancelled) setPortraitBytes(stripped.byteLength); })
            .catch(() => { if (!cancelled) setPortraitBytes(null); });
        return () => { cancelled = true; };
    }, [portrait]);

    // Re-serializing the whole card on every keystroke commit is cheap but not free with a big
    // lorebook, so let React schedule it at low priority.
    const deferredCardData = useDeferredValue(cardData);
    const cardDataBytes = useMemo(() => cardDataChunkBytes(deferredCardData), [deferredCardData]);

    const handleApplyCompression = (dataUrl) => {
        setCompressedPreview(dataUrl === preview ? null : dataUrl);
        storePreviewImage(dataUrl);
        setCompressDialogOpen(false);
    };

    useEffect(() => {
        const storedPreviewImage = localStorage.getItem("previewImage");
        if (storedPreviewImage){
            setPreview(storedPreviewImage);
        }
    }, []);

    return(
        <Container maxWidth={false}>
            <PortraitCompressionDialog
                open={compressDialogOpen}
                source={preview}
                onClose={() => setCompressDialogOpen(false)}
                onApply={handleApplyCompression}
            />
            <ConfirmationDialog 
                open={deleteConfirmation} 
                handleClose={() => setDeleteConfirmation(false)} 
                dialogTitle="Clear all fields?" 
                dialogContent="Are you sure you want to clear all fields? This action cannot be undone."
                handleConfirm={handleRemoveFile}
            />
            <ConfirmationDialog
                open={overwriteConfirmation}
                handleClose={closeOverwriteConfirmation}
                dialogTitle="Overwrite the current card?"
                dialogContent="Are you sure you want to overwrite the current fields with this JSON? This action cannot be undone."
                handleConfirm={handleOverwriteFile}
            />
            <ConfirmationDialog
                open={deleteGreetingConfirmation}
                handleClose={closeDeleteGreetingConfirmation}
                dialogTitle="Delete alternate greeting?"
                dialogContent={`Are you sure you want to delete Alt Greeting #${pendingGreeting + 1}? This action cannot be undone.`}
                handleConfirm={handleDeleteAltGreeting}
            />
            <ConfirmationDialog
                open={promoteGreeting}
                handleClose={closePromoteGreeting}
                dialogTitle="Promote alternate greeting?"
                dialogContent={`Are you sure you want to promote Alt Greeting #${pendingGreeting + 1} to the first message? The existing first message will be moved to Alternate Greeting #0's spot.`}
                handleConfirm={handlePromoteGreeting}
            />
            <ConfirmationDialog
                open={deleteEntryConfirmation}
                handleClose={() => setDeleteEntryConfirmation(false)}
                dialogTitle="Delete lorebook entry?"
                dialogContent={`Are you sure you want to delete Lorebook Entry #${pendingEntry + 1}? This action cannot be undone.`}
                handleConfirm={handleDeleteEntry}
            />
            <ConfirmationDialog
                open={backfillEntriesConfirmation}
                handleClose={() => setBackfillEntriesConfirmation(false)}
                dialogTitle="Backfill lorebook entry names and comments?"
                dialogContent={"The names and comments in your lorebook entries are mismatched. Would you like to backfill empty entries? Only the names and comments will be altered."}
                handleConfirm={backfillLorebookNames}
            />
            <ConfirmationDialog
                open={deleteGroupGreetingConfirmation}
                handleClose={closeGroupGreetingConfirmation}
                dialogTitle="Delete this group only greeting?"
                dialogContent={`Are you sure you want to delete Group Only Greeting #${pendingGroupGreeting + 1}? This action cannot be undone.`}
                handleConfirm={handleDeleteGroupGreeting}
            />
            <ConfirmationDialog 
                open={deleteLorebookConfirmation}
                handleClose={() => setDeleteLorebookConfirmation(false)}
                dialogTitle="Delete this lorebook"
                dialogContent={"Are you sure you want to delete this lorebook? This action cannot be undone."}
                handleConfirm={handleDeleteLorebook}
            />
            <ConfirmationDialog 
                open={purgeAsterisksConfirmation}
                handleClose={() => setPurgeAsterisksConfirmation(false)}
                dialogTitle="Purge asterisks from greetings?"
                dialogContent={"Are you sure you want to purge asterisks from all greetings? This macro should be used at your own risk as it does not discriminate and it cannot be undone."}
                handleConfirm={handlePurgeAsterisks}
            />
            <ConfirmationDialog
                open={findReplaceConfirmation}
                handleClose={closeFindReplaceConfirmation}
                dialogTitle="Find and replace the text provided?"
                dialogContent="Are you sure you want to find and replace the text you've provided? This will replace the specified text in the Description, Personality, Scenario, and ALL greetings. Note that there is no logic and is a quick and dirty find-and-replace. This action cannot be undone."
                handleConfirm={handleFindReplace}
            />
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{height: 64, px: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', overflowX: 'auto'}}
            >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <FileUpload acceptedFileTypes={".json,.png"} displayDeleteButton={true} file={file} fileChange={handleFileSelect} handleRemoveFile={() => setDeleteConfirmation(true)}/>
                    {saveFailed ?
                        <Tooltip title="Your edits are still here, but won't survive a page reload. Download the card to keep them.">
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{color: 'error.main', whiteSpace: 'nowrap'}}>
                                <ErrorOutline sx={{fontSize: 14}}/>
                                <Typography variant="caption">Not saved locally: browser storage is full</Typography>
                            </Stack>
                        </Tooltip> :
                        lastSavedAt &&
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{color: 'text.secondary', whiteSpace: 'nowrap'}}>
                                <CheckCircleOutline sx={{fontSize: 14}}/>
                                <Typography variant="caption">Saved locally at {lastSavedAt.toLocaleTimeString()}</Typography>
                            </Stack>
                    }
                </Stack>
                <Stack direction="row" alignItems="center" spacing={3}>
                    <FormControlLabel control={<Checkbox checked={displayImage} onChange={() => setDisplayImage(!displayImage)}/>} label="Show portrait" sx={{whiteSpace:"nowrap"}}/>
                    {file &&
                        <div>
                            <input accept={".json"} hidden id="json-upload" onChange={handleOverwriteClick} onClick={(event) => {event.target.value = null}} type="file"/>
                            <label htmlFor='json-upload'>
                                <Tooltip title="Overwrite the contents of this card with a JSON while retaining the display picture">
                                    <Button component="span" sx={{whiteSpace: "nowrap"}} variant="outlined">Overwrite with JSON</Button>
                                </Tooltip>
                            </label>
                        </div>
                    }
                    <Divider orientation="vertical" flexItem sx={{my: 1.5}}/>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {theme.palette.mode === "dark" ? <LightModeOutlined fontSize="small"/> : <LightMode fontSize="small"/>}
                        <Switch checked={theme.palette.mode === "dark"} onChange={toggleTheme}/>
                        {theme.palette.mode === "dark" ? <DarkMode fontSize="small"/> : <DarkModeOutlined fontSize="small"/>}
                    </Stack>
                </Stack>
            </Stack>
            <Box sx={{
                display: "flex",
                height: "calc(100vh - 64px - 48px)",
                m: 3,
                border: 1,
                borderColor: 'divider',
                borderRadius: 3,
                overflow: "hidden"
            }}>
                        {displayImage && <Container disableGutters style={{alignItems:"center", display:"flex", flexDirection: "column", flex:2, overflow:"auto", gap: 12}} sx={{p: 3}}>
                            <Box
                                onClick={() => previewImageRef.current.click()}
                                sx={{
                                    flex: 1,
                                    width: "100%",
                                    borderRadius: 2,
                                    border: 1,
                                    borderColor: 'divider',
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: 'pointer',
                                    bgcolor: theme.palette.mode === "dark" ? "#1e1f27" : "#efeef7",
                                    backgroundImage: theme.palette.mode === "dark"
                                        ? "linear-gradient(160deg, #23242e 0%, #1b1c24 100%)"
                                        : "linear-gradient(160deg, #ffffff 0%, #efeef7 100%)",
                                }}
                            >
                                {portrait !== default_avatar
                                    ? <img alt={file ? file.name : "Uploaded portrait"} src={portrait} style={{objectFit:'cover', width: "100%", height: "100%"}}/>
                                    : <PersonOutline sx={{fontSize: 96, color: theme.palette.mode === "dark" ? "#4a4c5c" : "#c3c2d4"}}/>
                                }
                            </Box>
                            <input
                                accept="image/*"
                                hidden
                                onChange={handlePreviewUpload}
                                ref={previewImageRef}
                                type="file"
                            />
                        </Container>}
                        <Container disableGutters maxWidth={false} style={{display:"flex", flexDirection:"column", flex:5, overflow:"hidden"}} sx={{p: 3}}>
                            <Tabs onChange={(event, newValue) => setTabValue(newValue)} value={tabValue} scrollButtons="auto" sx={{mb:2, flexShrink: 0}} variant="scrollable">
                                <Tab id={0} label="v1 Spec Fields"/>
                                <Tab id={1} label="Alt Greetings"/>
                                <Tab id={2} label="Creator Metadata"/>
                                <Tab id={3} label="Prompts"/>
                                <Tab id={4} label="Lorebook"/>
                                <Tab id={5} label="Group Greetings"/>
                                <Tab id={6} label="Macros"/>
                                <Tab id={7} label="Raw JSON"/>
                            </Tabs>
                            <Box sx={{flex: 1, overflow: 'auto', pr: 1, mr: -1}}>
                                <BasicFieldTabPanel
                                    curTab={tabValue}
                                    index={0}
                                    arrayToIterate={charMetadataFields}
                                />
                                <AltGreetingTabPanel
                                    curTab={tabValue}
                                    index={1}
                                    handleAltGreetingClick={handleAltGreetingClick}
                                    handlePromoteClick={handlePromoteClick}
                                />
                                <BasicFieldTabPanel
                                    curTab={tabValue}
                                    index={2}
                                    arrayToIterate={creatorMetadataFields}
                                />
                                <BasicFieldTabPanel
                                    curTab={tabValue}
                                    index={3}
                                    arrayToIterate={promptFields}

                                />
                                <LorebookPanel
                                    curTab={tabValue}
                                    index={4}
                                    handleDeleteEntryClick={handleDeleteEntryClick}
                                    handleDeleteLorebookClick={() => setDeleteLorebookConfirmation(true)}
                                    handleLorebookDownload={handleLorebookDownload}
                                    handleImport={(event) => handleFileSelect(event, true)}
                                />
                                <GroupGreetingPanel
                                    curTab={tabValue}
                                    index={5}
                                    handleGroupGreetingClick={handleGroupGreetingClick}
                                />
                                <MacrosPanel
                                    curTab={tabValue}
                                    index={6}
                                    handlePurgeClick={() => setPurgeAsterisksConfirmation(true)}
                                    handleFindReplaceClick={(val1, val2) => handleFindReplaceClick(val1, val2)}
                                />
                                <RawJsonPanel
                                    curTab={tabValue}
                                    index={7}
                                    onApply={(normalizedCardData) => {
                                        setPendingJson(normalizedCardData);
                                        setOverwriteConfirmation(true);
                                    }}
                                />
                            </Box>
                            <Stack direction="row" justifyContent="space-between" sx={{pt: 2, mt: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0}}>
                                <Button onClick={handleJsonDownload} variant="outlined">Download as JSON</Button>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    {portraitBytes !== null &&
                                        <Tooltip title={`Portrait ${formatBytes(portraitBytes)} + card data ${formatBytes(cardDataBytes)}`}>
                                            <Typography color="text.secondary" variant="body2" sx={{whiteSpace: 'nowrap'}}>
                                                PNG size: {formatBytes(portraitBytes + cardDataBytes)}
                                            </Typography>
                                        </Tooltip>
                                    }
                                    {preview !== default_avatar &&
                                        <Button onClick={() => setCompressDialogOpen(true)} sx={{whiteSpace: 'nowrap'}} variant="text">Compress portrait</Button>
                                    }
                                    <Button onClick={handlePngDownload} sx={{whiteSpace: 'nowrap'}} variant="contained">Download as PNG</Button>
                                </Stack>
                            </Stack>
                        </Container>
            </Box>
        </Container>
    );
}

export default TavernCardEditor;